import { useCountryContext } from '@archipro-website/localisation';

import { isHDEUnavailableInCountry } from '~/modules/hde/utils/hde-country-availability';
import {
    getPrivacyPolicyLink,
    getWebsiteTermsLink,
} from '~/utils/route-config';

import type { B2BPageConfig, B2BSection } from './types';
import { resolveB2BLink, resolveB2BLinks } from './config/resolve-links';
import { useOptOutOfDesktopOnly } from './hooks/useOptOutOfDesktopOnly';
import { B2BTopNav } from './components/B2BTopNav';
import { B2BFooter } from './components/B2BFooter';
import { B2BHero } from './sections/B2BHero';
import { B2BStatementBand } from './sections/B2BStatementBand';
import { B2BHeadline } from './sections/B2BHeadline';
import { B2BFeature } from './sections/B2BFeature';
import { B2BIntroBand } from './sections/B2BIntroBand';
import { B2BPartnersStrip } from './sections/B2BPartnersStrip';
import { B2BPillarAccordion } from './sections/B2BPillarAccordion';
import { B2BAudienceSegments } from './sections/B2BAudienceSegments';
import { B2BMediaBand } from './sections/B2BMediaBand';
import { B2BProfileShowcase } from './sections/B2BProfileShowcase';
import { B2BMediaProgram } from './sections/B2BMediaProgram';
import { B2BTestimonial } from './sections/B2BTestimonial';
import { LeadFormSection } from './lead-form/LeadFormSection';
import { LeadModal } from './lead-form/LeadModal';

export interface B2BLandingPageProps {
    config: B2BPageConfig;
    /**
     * Origin of the main consumer site for the current business host (e.g.
     * `https://archiproamerica.com`). Used to resolve the `mainSite` nav/footer
     * links so they navigate off the subdomain. See `config/resolve-links.ts`.
     */
    mainSiteOrigin: string;
}

/** Maps a body section config to its component. */
const renderSection = (
    section: B2BSection,
    index: number,
    assets: B2BPageConfig['assets'],
) => {
    const key = `${section.type}-${index}`;
    switch (section.type) {
        case 'statement-band':
            return (
                <B2BStatementBand key={key} section={section} assets={assets} />
            );
        case 'headline':
            return <B2BHeadline key={key} section={section} assets={assets} />;
        case 'feature':
            return <B2BFeature key={key} section={section} assets={assets} />;
        case 'intro-band':
            return <B2BIntroBand key={key} section={section} assets={assets} />;
        case 'partners':
            return (
                <B2BPartnersStrip key={key} section={section} assets={assets} />
            );
        case 'accordion':
            return (
                <B2BPillarAccordion
                    key={key}
                    section={section}
                    assets={assets}
                />
            );
        case 'audience':
            return (
                <B2BAudienceSegments
                    key={key}
                    section={section}
                    assets={assets}
                />
            );
        case 'media-band':
            return <B2BMediaBand key={key} section={section} assets={assets} />;
        case 'profile-showcase':
            return (
                <B2BProfileShowcase
                    key={key}
                    section={section}
                    assets={assets}
                />
            );
        case 'media-program':
            return (
                <B2BMediaProgram key={key} section={section} assets={assets} />
            );
        case 'testimonial':
            return (
                <B2BTestimonial key={key} section={section} assets={assets} />
            );
        default:
            return null;
    }
};

/**
 * Shared B2B landing-page template. Both `/business-brands` and
 * `/business-profs` render this with their own {@link B2BPageConfig}. The page
 * is: dedicated transparent top nav (over hero) → ordered body sections →
 * page-mode lead form → dedicated footer, with the lead modal mounted once.
 */
export const B2BLandingPage = ({
    config,
    mainSiteOrigin,
}: B2BLandingPageProps) => {
    const { assets } = config;
    const resolve = (key?: string) => (key ? assets[key] : undefined);

    useOptOutOfDesktopOnly();

    // Country-aware footer config:
    //  • Resolve `mainSite` links against the host-derived main-site origin so
    //    they leave the business subdomain (e.g. `/projects` ->
    //    `https://archiproamerica.com/projects`). Internal B2B links stay as
    //    on-subdomain Remix paths. See `config/resolve-links.ts`.
    //  • Mirror the top-nav HDE rule (AU-only hide). The B2B template targets
    //    the US launch but a NZ/AU visitor can land here too.
    //  • Inject Terms + Privacy Policy as country-aware legal links.
    const { code: countryCode } = useCountryContext();
    const hideHDE = isHDEUnavailableInCountry(countryCode);
    const topNavLinks = resolveB2BLinks(config.topNav.links, mainSiteOrigin);
    const secondaryCta = config.topNav.secondaryCta
        ? resolveB2BLink(config.topNav.secondaryCta, mainSiteOrigin)
        : undefined;
    const footerConfig = {
        ...config.footer,
        linkGroups: config.footer.linkGroups.map((group) => ({
            ...group,
            links: group.links
                .filter(
                    (link) => !hideHDE || link.label !== 'Home Design Evening',
                )
                .map((link) => resolveB2BLink(link, mainSiteOrigin)),
        })),
        legalLinks: [
            {
                label: 'Terms',
                href: getWebsiteTermsLink(countryCode),
                newTab: true,
                isNonRemixRoute: true,
            },
            {
                label: 'Privacy Policy',
                href: getPrivacyPolicyLink(countryCode),
                newTab: true,
                isNonRemixRoute: true,
            },
        ],
    };

    return (
        <div className="w-full bg-white">
            {/* Hero → all sections → page-mode lead form sit in a flex column
                with a vw-based gap matching Figma's consistent 260 px inter-
                section rhythm (260 / 19.2 = 13.5417vw, which is exactly 260 px
                at the 1920 design width and scales with the viewport). The
                Footer renders OUTSIDE this flex because Figma wraps it with the
                lead form in a single frame with no gap between them. */}
            <div className="flex w-full flex-col gap-[13.5417vw] max-md:gap-[7vw]">
                <B2BHero
                    headline={config.hero.headline}
                    backgroundSrc={
                        resolve(config.hero.backgroundAssetKey) ?? ''
                    }
                    isVideo={config.hero.isVideo}
                    posterSrc={resolve(config.hero.posterAssetKey)}
                    scrollCueLabel={config.hero.scrollCueLabel}
                    logomarkSrc={resolve(config.hero.logomarkAssetKey)}
                    topNav={
                        <B2BTopNav
                            tagline={config.topNav.tagline}
                            links={topNavLinks}
                            wordmarkSrc={resolve(
                                config.topNav.wordmarkAssetKey,
                            )}
                            secondaryCta={secondaryCta}
                            primaryCta={config.topNav.primaryCta}
                        />
                    }
                />

                {config.sections.map((section, index) =>
                    renderSection(section, index, assets),
                )}

                <LeadFormSection
                    finalCta={config.finalCta}
                    copy={config.leadForm}
                    backgroundSrc={resolve('leadFormBackground')}
                />
            </div>

            <B2BFooter
                config={footerConfig}
                wordmarkSrc={resolve(config.footer.wordmarkAssetKey)}
            />

            <LeadModal
                copy={config.leadForm}
                imageSrc={resolve(config.leadForm.modalImageAssetKey)}
                logomarkSrc={resolve('leadModalLogomark')}
                subtitle={config.finalCta.body}
            />
        </div>
    );
};
